import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    Popover,
    Row,
    Avatar,
    Image,
    Tooltip,
    Typography,
    Input,
    Space,
} from 'antd'
import { Icon } from '@iconify/react'
import { getInitials } from '../../../../utils/commonUtils'
import './index.css'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { teamListGet, teamState } from '../../../team/team.slice'
import { loginState } from '../../../login/login.slice'

const { Text } = Typography

const AssigneeSelectPopover = forwardRef(
    (
        {
            assigneeList = [],
            value = null,
            onChange = () => {},
            selectedAssignee = null,
            tooltip = true,
            tooltipTitle = '',
            iconSize = 24,
            placement = 'bottom',
            searchable = false,
            disabled = false,
            assigneeEmptyShow = true,
        },
        ref
    ) => {
        const [search, setSearch] = useState('')

        const dispatch = useDispatch()

        const { userProfile } = useSelector(loginState)
        const { skipPage: teamSkipPage } = useSelector(teamState)

        const [filteredAssignees, setFilteredAssignees] = useState([])
        const [loading, setLoading] = useState(false)

        const selected =
            selectedAssignee ||
            assigneeList?.find((item) =>
                typeof value === 'string'
                    ? item.value === value
                    : item === value
            )

        const fetchAssignees = useMemo(
            () =>
                debounce(async (query) => {
                    setLoading(true)
                    try {
                        const response = await dispatch(
                            teamListGet({
                                limit: 50,
                                skip: teamSkipPage,
                                projectId: userProfile?.activeProject?._id,
                                search: query,
                            })
                        )

                        if (response?.payload?.data) {
                            const list = response?.payload?.data || []

                            const mappedList = list.map((data) => ({
                                label: data?.name,
                                value: data?._id,
                                profilePicture: data?.profilePicture,
                                email: data?.email,
                            }))

                            setFilteredAssignees(
                                mappedList.filter(
                                    (item) => item.value !== selected?.value
                                )
                            )
                        } else {
                            setFilteredAssignees([])
                        }
                    } catch (err) {
                        console.error('Failed to fetch assignees', err)
                        setFilteredAssignees([])
                    } finally {
                        setLoading(false)
                    }
                }, 1000),
            [
                dispatch,
                teamSkipPage,
                userProfile?.activeProject?._id,
                selected?.value,
            ]
        )

        useEffect(() => {
            if (search.trim()) {
                fetchAssignees(search)
            } else {
                setFilteredAssignees(
                    assigneeList.filter(
                        (item) => item.value !== selected?.value
                    )
                )
            }
        }, [search, selected, fetchAssignees])

        useEffect(() => {
            return () => {
                fetchAssignees.cancel()
            }
        }, [fetchAssignees])

        const renderAssigneeOption = (option) => (
            <Row
                key={option?.value}
                className={'popOverClick assigneeOption'}
                style={{
                    padding: 10,
                    cursor: 'pointer',
                    borderRadius: 5,
                    alignItems: 'center',
                    columnGap: 10,
                }}
                onClick={() => onChange(option.value || option, option)}
            >
                {option?.profilePicture ? (
                    <Image
                        src={option.profilePicture}
                        width={30}
                        height={30}
                        preview={false}
                        style={{
                            borderRadius: '50%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    <Avatar
                        size={30}
                        style={{
                            backgroundColor: 'rgb(0 10 129 / 25%)',
                            fontSize: 12,
                        }}
                    >
                        {getInitials(option?.label)}
                    </Avatar>
                )}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        fontWeight: 500,
                    }}
                >
                    <Text style={{ fontSize: 14 }}>{option?.label}</Text>
                    <Text style={{ fontSize: 12, color: '#858080' }}>
                        {option?.email}
                    </Text>
                </div>
            </Row>
        )

        const popoverContent = (
            <div style={{ minWidth: 220, maxHeight: 250 }}>
                {searchable && (
                    <div className="inputSearchWrapperPop">
                        <Input
                            size="small"
                            placeholder="Search Assignee"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={'inputSearchPop'}
                        />
                    </div>
                )}
                {filteredAssignees.length > 0 ? (
                    filteredAssignees.map((option) =>
                        renderAssigneeOption(option)
                    )
                ) : (
                    <Row
                        style={{
                            padding: 10,
                            borderRadius: 5,
                            alignItems: 'center',
                            columnGap: 10,
                        }}
                    >
                        <Text type="secondary">No assignee found</Text>
                    </Row>
                )}
            </div>
        )

        const renderTrigger = () => {
            if (selected?.profilePicture) {
                return assigneeEmptyShow ? (
                    <Row
                        align="middle"
                        style={{
                            columnGap: 4,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <Image
                            src={selected.profilePicture}
                            width={iconSize}
                            height={iconSize}
                            preview={false}
                            style={{
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                        />
                        <Text style={{ fontSize: 14 }}>{selected?.label}</Text>
                    </Row>
                ) : (
                    <Image
                        src={selected.profilePicture}
                        width={iconSize}
                        height={iconSize}
                        preview={false}
                        style={{
                            borderRadius: '50%',
                            objectFit: 'cover',
                        }}
                    />
                )
            } else if (selected?.label || selected?.name) {
                const name = selected.label || selected.name
                return assigneeEmptyShow ? (
                    <Row
                        align="middle"
                        style={{
                            columnGap: 4,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <Avatar
                            size={iconSize}
                            style={{
                                backgroundColor: 'rgb(0 10 129 / 25%)',
                                fontSize: iconSize / 2,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {getInitials(name)}
                        </Avatar>
                        <Text style={{ fontSize: 14 }}>{selected?.label}</Text>
                    </Row>
                ) : (
                    <Avatar
                        size={iconSize}
                        style={{
                            backgroundColor: 'rgb(0 10 129 / 25%)',
                            fontSize: iconSize / 2,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {getInitials(name)}
                    </Avatar>
                )
            }
            if (selected === undefined) {
                return assigneeEmptyShow ? (
                    <Row
                        align="middle"
                        style={{
                            columnGap: 4,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <Space>
                            <Icon
                                icon="lets-icons:user-cicrle-light"
                                width={iconSize}
                                height={iconSize}
                                style={{
                                    cursor: disabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                }}
                            />
                            <Text style={{ fontSize: 14 }}>Unassigned</Text>
                        </Space>
                    </Row>
                ) : (
                    <Icon
                        icon="lets-icons:user-cicrle-light"
                        width={iconSize}
                        height={iconSize}
                        style={{
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    />
                )
            }
        }

        const wrappedTrigger = tooltip ? (
            <Tooltip
                title={tooltipTitle || selected?.label || 'Unassigned'}
                arrow={false}
                showArrow={false}
                placement="bottom"
                classNames={{
                    body: 'tooltip-global',
                }}
            >
                <div>{renderTrigger()}</div>
            </Tooltip>
        ) : (
            renderTrigger()
        )

        return (
            <Popover
                content={!disabled && popoverContent}
                trigger="click"
                placement={placement}
                arrow={false}
                getPopupContainer={() => ref?.current}
                styles={{
                    body: {
                        maxHeight: '300px',
                        overflowY: 'auto',
                        padding: 0,
                        scrollbarColor: '#888 #fff',
                    },
                }}
            >
                <div
                    ref={ref}
                    style={{
                        display: !assigneeEmptyShow ? 'flex' : 'inline-block',
                    }}
                >
                    {wrappedTrigger}
                </div>
            </Popover>
        )
    }
)

export default React.memo(AssigneeSelectPopover)
